<script lang="ts" setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useI18n } from 'vue-i18n'
import { ZoomIn, ZoomOut } from '@element-plus/icons-vue'
import { Done } from '@xpos/icons-svg'

const $props = defineProps<{
  modelValue: boolean
  imageSrc: string
  aspectRatio?: number
}>()
const imgSrc = computed(() => $props.imageSrc)
const $emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', blob: Blob): void
}>()
const { t } = useI18n()
const cropperRef = ref<InstanceType<typeof Cropper>>()
const visible = ref($props.modelValue)
const stencilProps = computed(() => $props.aspectRatio ? { aspectRatio: $props.aspectRatio } : {})
const zoom = ref(1)
const isUploading = ref(false)
// Tracks initial visible area width for accurate zoom calculation
const baseVisibleWidth = ref(0)
const isSliderDragging = ref(false)
let zoomDebounceTimer: ReturnType<typeof setTimeout> | null = null

watch(() => $props.modelValue, (val) => { visible.value = val })
watch(visible, (val) => { if (!val) $emit('update:modelValue', false) })

const handleClose = () => { $emit('update:modelValue', false) }
const close = () => { isUploading.value = false; handleClose() } // call on upload success
const onError = () => { isUploading.value = false } // call on upload failure — keeps dialog open for retry
// Captures initial visible area when cropper is ready
const handleCropperReady = () => {
  const result = cropperRef.value?.getResult()
  if (!result?.visibleArea) return
  baseVisibleWidth.value = result.visibleArea.width
  zoom.value = 1
}
// Syncs zoom slider on cropper change (e.g. user pinch/scroll)
const handleCropperChange = () => {
  if (isSliderDragging.value || !baseVisibleWidth.value) return
  const result = cropperRef.value?.getResult()
  if (!result?.visibleArea) return
  const currentZoom = baseVisibleWidth.value / result.visibleArea.width
  zoom.value = Math.min(3, Math.max(1, Math.round(currentZoom * 10) / 10))
}
// Executes actual zoom operation
const applyZoom = (targetZoom: number) => {
  if (!cropperRef.value || !baseVisibleWidth.value) return
  const result = cropperRef.value.getResult()
  if (!result?.visibleArea) return
  const currentZoom = baseVisibleWidth.value / result.visibleArea.width
  const zoomFactor = targetZoom / currentZoom
  cropperRef.value.zoom(zoomFactor)
  setTimeout(() => { isSliderDragging.value = false }, 100)
}
// Handles zoom change via slider with debounce
const handleZoomChange = (value: number) => {
  isSliderDragging.value = true
  zoom.value = value
  if (zoomDebounceTimer) clearTimeout(zoomDebounceTimer)
  zoomDebounceTimer = setTimeout(() => applyZoom(value), 300)
}
// Zoom in by 0.2 with debounce
const handleZoomIn = () => {
  if (zoom.value >= 3) return
  const newZoom = Math.min(Math.round((zoom.value + 0.2) * 10) / 10, 3)
  handleZoomChange(newZoom)
}
// Zoom out by 0.2 with debounce
const handleZoomOut = () => {
  if (zoom.value <= 1) return
  const newZoom = Math.max(Math.round((zoom.value - 0.2) * 10) / 10, 1)
  handleZoomChange(newZoom)
}
// Emits blob to parent, shows loading; parent calls close() on success or onError() on failure
const handleConfirm = () => {
  const { canvas } = cropperRef.value?.getResult() || {}
  if (!canvas) return
  canvas.toBlob((blob: Blob | null) => {
    if (blob) {
      isUploading.value = true
      $emit('confirm', blob)
    }
  }, 'image/jpeg', 0.9)
}

onUnmounted(() => { if (zoomDebounceTimer) clearTimeout(zoomDebounceTimer) })
defineExpose({ close, onError })
</script>

<template>
  <x-dialog v-model="visible" width="770px" :title="t('components.FormWidget.UploadWidget.crop_image')" @close="handleClose">
    <template #headerRight>
      <el-button @click="handleConfirm" color="#80BA5C">
        <el-icon size="20" color="#fff"><Done /></el-icon>
      </el-button>
    </template>
    <div class="flex flex-col items-center gap-2.5 w-full">
      <!-- Cropper area -->
      <Cropper v-if="imgSrc" ref="cropperRef" class="cropper" :src="imgSrc" :stencil-props="stencilProps" @ready="handleCropperReady" @change="handleCropperChange" />
      <!-- Zoom controls -->
      <div class="flex justify-center items-center gap-6 w-full h-[50px]">
        <el-button class="!w-[50px] !h-[50px] !p-0" :class="zoom <= 1 ? '!bg-[#CDCDCD]' : '!bg-[#52599D]'" type="primary" @click="handleZoomOut" :disabled="zoom <= 1">
          <el-icon :size="20" color="white"><ZoomOut /></el-icon>
        </el-button>
        <el-slider v-model="zoom" :min="1" :max="3" :step="0.1" class="!w-[240px]" @input="handleZoomChange" />
        <el-button class="!w-[50px] !h-[50px] !p-0" :class="zoom >= 3 ? '!bg-[#CDCDCD]' : '!bg-[#52599D]'" type="primary" @click="handleZoomIn" :disabled="zoom >= 3">
          <el-icon :size="20" color="white"><ZoomIn /></el-icon>
        </el-button>
      </div>
    </div>
  </x-dialog>
</template>

<style scoped>
.cropper { width: 100%; max-width: 730px; height: 477px; }
</style>
